import { useEffect, useRef, useState } from 'react';

function formatTime(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

function alertKey(a) {
  return `${a.article_id}__${a.symbol}`;
}

function deduplicateAlerts(alerts) {
  const map = {};
  for (const a of alerts) {
    if (!map[a.symbol] || new Date(a.timestamp) > new Date(map[a.symbol].timestamp)) {
      map[a.symbol] = a;
    }
  }
  return Object.values(map).sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
}

function AlertPopup({ newAlerts, onDismiss }) {
  useEffect(() => {
    if (newAlerts.length === 0) return;
    const t = setTimeout(onDismiss, 8000);
    return () => clearTimeout(t);
  }, [newAlerts, onDismiss]);

  if (newAlerts.length === 0) return null;

  return (
    <div style={{ animation: 'slideIn 0.25s ease-out both' }}
         className="fixed top-4 right-4 z-50 w-80">
      <div className="bg-white border border-red-500/50 rounded-xl shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-3 py-2 bg-red-500/8 border-b border-red-500/20">
          <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
            <span className="text-xs font-bold text-red-600 uppercase tracking-wider">
              Sentiment Alert{newAlerts.length > 1 ? `s (${newAlerts.length})` : ''}
            </span>
          </div>
          <button
            onClick={onDismiss}
            className="text-slate-400 hover:text-slate-600 transition-colors text-xs"
          >✕</button>
        </div>

        <div className="divide-y divide-slate-200 max-h-64 overflow-y-auto">
          {newAlerts.map(a => (
            <div key={alertKey(a)} className="px-3 py-2.5">
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-1.5">
                  <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold ${
                    a.alert ? 'bg-red-500/20 text-red-600' : 'bg-orange-500/15 text-orange-600'
                  }`}>
                    {a.symbol}
                  </span>
                  <span className={`text-[10px] font-bold ${a.alert ? 'text-red-600' : 'text-orange-600'}`}>
                    {a.score.toFixed(2)}
                  </span>
                  {a.alert && (
                    <span className="text-[9px] font-bold px-1 py-0.5 rounded bg-red-500/15 text-red-600 uppercase tracking-wide">
                      ALERT
                    </span>
                  )}
                </div>
                <span className="text-[9px] text-slate-500 tabular-nums">{formatTime(a.timestamp)}</span>
              </div>
              <p className="text-[10px] text-slate-600 leading-snug line-clamp-2">{a.headline}</p>
              {a.source && <span className="text-[9px] text-slate-400">{a.source}</span>}
            </div>
          ))}
        </div>

        <div className="px-3 py-1.5 bg-slate-100/60 text-center">
          <p className="text-[9px] text-slate-400">Auto-dismisses in 8s · See alert bar below</p>
        </div>
      </div>
    </div>
  );
}

function AlertBar({ alerts }) {
  const [expanded, setExpanded] = useState(false);

  if (alerts.length === 0) return null;

  const criticalCount = alerts.filter(a => a.alert).length;
  const warnCount = alerts.length - criticalCount;

  return (
    <div className="bg-white border border-red-500/40 rounded-xl overflow-hidden">
      <button
        onClick={() => setExpanded(e => !e)}
        className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-red-500/5 transition-colors"
      >
        <div className="flex items-center gap-3">
          <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0" />
          <span className="text-xs font-bold text-red-600 uppercase tracking-wider">Sentiment Alerts</span>
          <div className="flex items-center gap-1.5">
            {criticalCount > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-red-500/15 text-red-600">
                {criticalCount} critical
              </span>
            )}
            {warnCount > 0 && (
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold bg-orange-500/15 text-orange-600">
                {warnCount} warning
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500">
            {alerts.length} asset{alerts.length !== 1 ? 's' : ''} below threshold
          </span>
          <span className="text-slate-400 text-xs">{expanded ? '▲' : '▼'}</span>
        </div>
      </button>

      {expanded && (
        <div className="divide-y divide-slate-200">
          <div className="grid grid-cols-[80px_64px_56px_1fr_110px] gap-3 px-4 py-1.5 bg-slate-100/70">
            {['Symbol', 'Score', 'Level', 'Headline', 'Time'].map(h => (
              <span key={h} className="text-[9px] font-bold text-slate-500 uppercase tracking-wider">{h}</span>
            ))}
          </div>

          {alerts.map(a => (
            <div
              key={alertKey(a)}
              className={`grid grid-cols-[80px_64px_56px_1fr_110px] gap-3 px-4 py-2 items-center transition-colors ${
                a.alert ? 'bg-red-500/5 hover:bg-red-500/8' : 'hover:bg-slate-50'
              }`}
            >
              <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold w-fit ${
                a.alert ? 'bg-red-500/20 text-red-600' : 'bg-orange-500/15 text-orange-600'
              }`}>
                {a.symbol}
              </span>

              <span className={`text-xs font-bold tabular-nums ${a.alert ? 'text-red-600' : 'text-orange-600'}`}>
                {a.score.toFixed(3)}
              </span>

              {a.alert ? (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-500/15 text-red-600 uppercase tracking-wide w-fit">
                  ALERT
                </span>
              ) : (
                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-orange-500/15 text-orange-600 uppercase tracking-wide w-fit">
                  WARN
                </span>
              )}

              <p className="text-[10px] text-slate-600 truncate" title={a.headline}>{a.headline}</p>

              <div className="text-right">
                <span className="text-[10px] text-slate-500 tabular-nums">{formatTime(a.timestamp)}</span>
                {a.source && <span className="block text-[9px] text-slate-400">{a.source}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AlertSystem({ alerts = [] }) {
  const [popupAlerts, setPopupAlerts] = useState([]);
  const seenKeysRef = useRef(new Set());

  useEffect(() => {
    const incoming = [];
    for (const a of alerts) {
      const k = alertKey(a);
      if (!seenKeysRef.current.has(k)) {
        seenKeysRef.current.add(k);
        incoming.push(a);
      }
    }
    if (incoming.length > 0) setPopupAlerts(incoming.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
  }, [alerts]);

  return (
    <>
      <style>{`@keyframes slideIn{from{opacity:0;transform:translateX(1.5rem)}to{opacity:1;transform:translateX(0)}}`}</style>
      <AlertPopup newAlerts={popupAlerts} onDismiss={() => setPopupAlerts([])} />
      <AlertBar alerts={deduplicateAlerts(alerts)} />
    </>
  );
}
